import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/status.enum';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';

/**
 * Icon configuration interface
 */
interface IconConfig {
  icon: string;
  iconType: 'svg' | 'font';
  tooltip: string;
  color: string;
}

/**
 * Order Status Icons Cell Renderer Component
 *
 * Renders three visual indicators horizontally in one cell:
 * 1. Status Icon (statusId) - Order status (Anulado, Creado, Aprobado, etc.)
 * 2. StatusERP Icon (statusERP) - ERP integration status
 * 3. Type Badge (subApplication) - Source application badge (B/A/E)
 *
 * Copied from: src/app/modules/components/monitor/table-load-list/table-load-list.component.html
 * - Lines 45-84: status column
 * - Lines 86-103: statusERP column
 * - Lines 105-141: type column
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   colId: 'statusIcons',
 *   headerName: 'Estado',
 *   cellRenderer: OrderStatusIconsCellRendererComponent,
 *   width: 120
 * }
 * ```
 */
@Component({
  selector: "app-order-status-icons-cell-renderer",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-x-1">
      <div class="flex items-center w-full">
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.INACTIVE"
          svgIcon="heroicons_solid:x-circle"
          matTooltip="Anulado"
          style="color: rgb(141, 19, 19); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ACTIVE"
          [attr.aria-label]="getIconConfig(params?.data?.type.code).tooltip"
          [matTooltip]="getIconConfig(params?.data?.type.code).tooltip"
          [style.color]="getIconConfig(params?.data?.type.code).color"
          style="cursor: pointer"
          [svgIcon]="
            getIconConfig(params?.data?.type.code).iconType === 'svg'
              ? getIconConfig(params?.data?.type.code).icon
              : null
          "
        >
          <span
            *ngIf="getIconConfig(params?.data?.type.code).iconType === 'font'"
          >
            {{ getIconConfig(params?.data?.type.code).icon }}
          </span>
        </mat-icon>
        <!-- <mat-icon
              *ngIf="params?.data?.statusId == StatusEnum.ACTIVE"
              svgIcon="heroicons_solid:check-circle"
              matTooltip="Creado"
              style="color: rgb(65, 106, 218); cursor: pointer"
            ></mat-icon> -->
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_IN_PROGRESS"
          svgIcon="heroicons_solid:play-circle"
          matTooltip="En proceso de aprobación"
          style="color: rgb(236, 199, 58); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_APPROVED"
          svgIcon="heroicons_solid:check-circle"
          matTooltip="Aprobado"
          style="color: rgb(9, 133, 50); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_REJECTED"
          svgIcon="heroicons_solid:x-circle"
          matTooltip="Rechazado"
          style="color: rgb(255, 0, 0); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_DELIVERED"
          svgIcon="mat_solid:checklist_rtl"
          matTooltip="Entregado completamente al cliente"
          style="color: rgb(9, 133, 50); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_PARTIAL_DELIVERED"
          svgIcon="mat_solid:check"
          matTooltip="Entregado parcialmente al cliente"
          style="color: rgb(255, 136, 0); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_DELIVERY_REJECTED"
          svgIcon="mat_solid:indeterminate_check_box"
          matTooltip="No entregado al cliente"
          style="color: rgb(255, 55, 0); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusId == StatusEnum.ORDER_PARTIAL_APPROVED"
          svgIcon="heroicons_solid:check-circle"
          matTooltip="Aprobado Parcialmente"
          style="color: rgb(102, 205, 170); cursor: pointer"
        ></mat-icon>
      </div>

      <div class="flex items-center w-full">
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_PENDING || !params?.data?.statusERP"
          svgIcon="heroicons_solid:clock"
          matTooltip="Pendiente envío a ERP"
          style="color: rgb(233, 124, 61); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_SEND"
          svgIcon="heroicons_solid:document-arrow-up"
          matTooltip="Enviado a ERP"
          style="color: rgb(65, 106, 218); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_RECEIVED"
          svgIcon="heroicons_solid:document-arrow-down"
          matTooltip="Recibido por ERP"
          style="color: rgb(236, 199, 58); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_CREATED"
          svgIcon="heroicons_solid:document-check"
          matTooltip="Creado en ERP"
          style="color: rgb(9, 133, 50); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_ERROR"
          svgIcon="heroicons_solid:exclamation-triangle"
          matTooltip="Error enviando a ERP"
          style="color: rgb(141, 19, 19); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_PENDING_FOR_UPDATE"
          svgIcon="heroicons_solid:clock"
          matTooltip="Mod. pendiente de replicar"
          style="color: rgb(148, 46, 139); cursor: pointer"
        ></mat-icon>
        <mat-icon
          *ngIf="params?.data?.statusERP == OrderStatusERPEnum.ORDER_ERROR_ERP"
          svgIcon="heroicons_solid:exclamation-triangle"
          matTooltip="Error en ERP"
          style="color: rgb(255, 0, 0); cursor: pointer"
        ></mat-icon>
      </div>
      <div class="flex items-center w-full">
        <div
          class="!h-fit"
          *ngIf="params.data?.subApplication?.code == 'CSMOBILE-BACKOFFICE'"
          style="
                background-color: rgb(40, 101, 141);
                color: white;
                padding: 0px 5px 0px 5px;
                border-radius: 5px;
                margin-left: 0px;
                margin-right: 0px;
                cursor: pointer;
              "
          [matTooltip]="element?.subApplication?.name"
        >
          B
        </div>
        <div
          class="!h-fit"
          *ngIf="params.data?.subApplication?.code == 'CSMOBILE-APP'"
          style="
                background-color: rgb(218, 41, 28);
                color: white;
                padding: 0px 5px 0px 5px;
                border-radius: 5px;
                margin-left: 0px;
                margin-right: 0px;
                cursor: pointer;
              "
          [matTooltip]="element?.subApplication?.name"
        >
          A
        </div>
        <div
          class="!h-fit"
          *ngIf="params.data?.subApplication?.code == 'CSMOBILE-ERP'"
          style="
                background-color: rgb(255, 123, 0);
                color: white;
                padding: 0px 5px 0px 5px;
                border-radius: 5px;
                margin-left: 0px;
                margin-right: 0px;
                cursor: pointer;
              "
          [matTooltip]="'ERP'"
        >
          E
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderStatusIconsCellRendererComponent
  implements CellRenderer<any, any>
{
  // Expose enums for template
  StatusEnum = StatusEnum;
  OrderStatusERPEnum = OrderStatusERPEnum;

  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Icon configuration for different document types
   * Copied from table-load-list.component.ts (lines 1716-1747)
   */
  private iconConfig: { [key: string]: IconConfig } = {
    [DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO]: {
      icon: "heroicons_solid:exclamation-circle",
      iconType: "svg",
      tooltip: "En espera de aprobación",
      color: "rgb(236, 199, 58)",
    },
    [DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO]: {
      icon: "heroicons_solid:exclamation-circle",
      iconType: "svg",
      tooltip: "En espera de aprobación",
      color: "rgb(236, 199, 58)",
    },
    [DocumentTypeEnum.DEVOLUCION_DE_PRODUCTO]: {
      icon: "local_shipping",
      iconType: "font",
      tooltip: "En espera de recolección",
      color: "rgb(236, 199, 58)",
    },
    [DocumentTypeEnum.CAMBIO_DE_PRODUCTO]: {
      icon: "swap_vert",
      iconType: "font",
      tooltip: "En espera de cambio de producto",
      color: "rgb(236, 199, 58)",
    },
    default: {
      icon: "heroicons_solid:check-circle",
      iconType: "svg",
      tooltip: "Creado",
      color: "rgb(65, 106, 218)",
    },
  };

  /**
   * Order data
   */
  get order(): any {
    return this.params?.data;
  }

  /**
   * Status ID
   */
  get statusId(): number {
    return this.order?.statusId;
  }

  /**
   * Status ERP
   */
  get statusERP(): number {
    return this.order?.statusERP;
  }

  /**
   * Sub-application code
   */
  get subApplicationCode(): string {
    return this.order?.subApplication?.code;
  }

  /**
   * Sub-application name
   */
  get subApplicationName(): string {
    return this.order?.subApplication?.name;
  }

  /**
   * Document type code
   */
  get typeCode(): string {
    return this.order?.type?.code;
  }

  /**
   * Get icon configuration for statusId === 1 based on document type
   */
  get statusIconConfig(): IconConfig {
    return this.iconConfig[this.typeCode] || this.iconConfig.default;
  }

  getIconConfig(code: string) {
    return this.iconConfig[code] || this.iconConfig.default;
  }
}
